import React, { FC } from 'react';

import debounce from 'debounce-promise';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu } from '@nhl/nhl-react-ui/lib/Menu';
import '@nhl/nhl-react-ui/lib/Menu/styles.css';

import teamWordmarks from '../../../images/wordmarks';
import { franchiseSelectors, useFranchisesQuery } from '../../../redux/ducks/franchises';
import recordsAPI from '../../../utils/recordsAPI';
import { TeamDisplayModule } from '../index';

import './styles.scss';

window.onscroll = () => {
    if (document.querySelector('header nav') !== null) {
        const nav = document.querySelector('header nav')?.classList;
        nav?.add('sticky');
    }
};

type RecordOption = {
    label: string;
    options: string[];
    categoryId: string;
    typeId: string;
};

const createAllStarConfig = (type, format) => ({ type, format });
const allStarMap = {
    'all-star-skater-3-on-3': createAllStarConfig('skater', '3-on-3-records'),
    'all-star-skater-5-on-5': createAllStarConfig('skater', '5-on-5-records'),
    'all-star-goaltender-3-on-3': createAllStarConfig('goaltender', '3-on-3-records'),
    'all-star-goaltender-5-on-5': createAllStarConfig('goaltender', '5-on-5-records'),
    'all-star-team-3-on-3': createAllStarConfig('team', '3-on-3-records'),
    'all-star-team-5-on-5': createAllStarConfig('team', '5-on-5-records'),
};

const recordsLogo = 'https://wsr.nhle.com/images/records-logo.svg';

const HeaderMenu: FC = () => {
    const { data: franchisesList = [] } = useFranchisesQuery({});
    const triCode = window.recordsSettings.franchiseTricode.toUpperCase();
    const franchise = franchisesList.find((franchise) => franchise.teamAbbrev === triCode);

    const navigate = useNavigate();
    const location = useLocation();

    const currentTeam = franchiseSelectors.getActiveTeam(franchise) || '';
    const currentFranchiseSlug = franchise?.slug;

    const franchiseTeamsDropdown = (franchiseTeams) =>
        franchiseTeams &&
        franchiseTeams.map((franchise) => {
            const updatedPath = location.pathname.replace(currentFranchiseSlug, franchise.slug);
            const team = franchiseSelectors.getActiveTeam(franchise);

            return (
                <a href={`/${franchise.teamAbbrev.toLowerCase()}${updatedPath}`} key={franchise.id}>
                    <TeamDisplayModule
                        team={team}
                        useInTable
                        displayTricode={window.innerWidth < 450}
                        displayNameOverride={team.fullName}
                    />
                </a>
            );
        });

    const activeFranchises = franchisesList.filter((franchise) => !franchise.lastSeasonId);
    const inactiveFranchises = franchisesList.filter((franchise) => franchise.lastSeasonId);

    const franchiseLink = currentTeam && (
        <div className="franchise-link dropdown">
            <Link to="/" className="dropbtn">
                {teamWordmarks[currentTeam.triCode] ? (
                    <img
                        className="wordmark"
                        src={teamWordmarks[currentTeam.triCode]}
                        alt={`${currentTeam.triCode} Wordmark`}
                    />
                ) : (
                    <TeamDisplayModule team={currentTeam} className="team-module" />
                )}
                <FontAwesomeIcon icon={solid('caret-down')} className="wordmarkCaret" />
            </Link>
            <div className="dropdown-menuContent">
                <div className="menuContent-subHeading">Active Franchises</div>
                {franchiseTeamsDropdown(activeFranchises)}
                <div className="menuContent-subHeading">Inactive Franchises</div>
                {franchiseTeamsDropdown(inactiveFranchises)}
            </div>
        </div>
    );

    const logo = (
        <div className="records-logo">
            {window.recordsSettings.franchiseTricode === 'nhl' ? (
                <Link to="/">
                    <img src={recordsLogo} />
                </Link>
            ) : (
                <div className="links-container">
                    <a href={window.location.origin}>
                        <img src={recordsLogo} />
                    </a>
                    <div className="link-divider">&nbsp;</div>
                    {franchiseLink}
                </div>
            )}
        </div>
    );

    const navigateToRecord = (recordDetail) => {
        let url = `/records/${recordDetail.recordCategory.short_description}`;
        if (recordDetail.recordCategory.short_description !== recordDetail.recordType.descriptionKey) {
            url += `/${recordDetail.recordType.descriptionKey}`;
        }
        if (recordDetail.recordType.descriptionKey !== recordDetail.descriptionKey) {
            url += `/${recordDetail.descriptionKey}`;
        }

        const allStarKey = Object.keys(allStarMap).find((key) => recordDetail.descriptionKey.indexOf(key) === 0);
        if (allStarKey) {
            const recordTypeKey = recordDetail.recordType.descriptionKey;
            const { format, type } = allStarMap[allStarKey];
            url = `/all-star/${format}/${type}/${recordTypeKey}/${recordDetail.descriptionKey}`;
        }
        navigate(url);
    };

    const loadRecordOptions = async (query) => {
        if (query.length >= 3) {
            try {
                const { data } = await recordsAPI.searchRecordsData(query);
                const groupedData: Array<RecordOption> = [];

                // Here we're just grouping the records. Since the format that react-select requires is so different
                // and AgRest can only mapBy one level anyway we just do it on the client.
                // The groupedData array consists of objects with a label property, an options property, a category
                // property, and a a type property. The last two are used only for matching a record to its
                // respective group. The label will be a string of the template
                // '[record category deccription] / [record type description]'. All records which are of this
                // category and type will be put into the options array corresponding to that the cat/type pair's
                // object.
                data.forEach((record) => {
                    const groupLabel = `${record.recordCategory.description} / ${record.recordType.description}`;
                    let group = groupedData.find(
                        (g) => g.categoryId === record.recordCategory.id && g.typeId === record.recordType.id
                    );

                    if (!group) {
                        group = {
                            label: groupLabel,
                            options: [record],
                            categoryId: record.recordCategory.id,
                            typeId: record.recordType.id,
                        };
                        groupedData.push(group);
                    } else {
                        group.options.push(record);
                    }
                });

                return groupedData;
            } catch (error) {
                console.error(error);
            }
        }

        return [];
    };

    const search = location.pathname.includes('records') ? (
        <div className="search">
            <AsyncSelect
                cacheOptions
                loadOptions={debounce(loadRecordOptions, 500)}
                onChange={navigateToRecord}
                getOptionLabel={(option) => option.description}
                getOptionValue={(option) => option.id}
                placeholder="Search for a Record..."
                value={null}
            />
        </div>
    ) : null;

    return (
        <Menu
            menuHeight={80}
            logo={logo}
            location={location}
            isPublic
            append={search}
            restUrl={`${window.recordsSettings.franchiseScopedRestUrl}/menu?cayenneExp=parent=null&include=children&include=children.children`}
        />
    );
};

export default HeaderMenu;
